import { observer } from 'mobx-react';
import { Select } from 'antd';
import { useEffect } from 'react';
import { PageTopBar } from '~components/page-top-bar';
import { AnalyticsSearchOptions } from '~components/analytics-search-options';
import { useApplicationSelectionStore, useAuthStore } from '~contexts/mobx';
import { useNavigation } from '~utils/use-navigation';
import { useSearchParams } from '~utils/use-search-params';
import { SectionHeader } from '~analytics-components/section-header';
import { KpiLoadProgressBar } from '~analytics-components/kpi-load-progress-bar';
import { KpiCardFromSpec } from '~containers/analytics/kpis/kpi-card-from-spec';
import { OverviewToolbar } from '~containers/analytics/overview-page/overview-toolbar';
import { usePageFilter } from '~containers/analytics/overview-page/use-page-filter';
import type { PageFilterQuery } from '~containers/analytics/overview-page/use-page-filter';
import type { KpiCardSpec } from '~containers/analytics/kpis/card-spec';
import { lastSelectableDay } from '~containers/analytics/kpis/periods';
import {
    BOOKLET_COLLECTION_CARDS,
    COIN_POPULARITY_CARDS,
    ENROLLED_PLAYERS_CARDS,
} from './cards';
import { useNhlCoinsEdition } from './use-nhl-coins-edition';
import style from './style.module.scss';

/**
 * NHL Coins analytics dashboard: the cards of the selected edition, over the
 * edition's own window until the reader picks another period.
 */
export const NhlCoinsAnalyticsPage = observer(() => {
    const authStore = useAuthStore();
    const navigation = useNavigation();
    const applicationSelectionStore = useApplicationSelectionStore();
    const search = useSearchParams<PageFilterQuery>();
    const {
        editions,
        edition,
        select,
        campaign,
        context,
    } = useNhlCoinsEdition();

    const {
        filter,
        setPeriod,
        setPreset,
        setRegion,
        setCompare,
    } = usePageFilter({
        search,
        region: {
            appTarget: applicationSelectionStore.appTarget,
            defaultProvincesCsv:
                applicationSelectionStore.getProvincesFromSelectedApplication,
        },
        defaultPeriod: 'campaign',
        campaign: campaign &&
            edition && {
                window: campaign,
                campaignId: edition.id,
            },
    });

    useEffect(() => {
        if (!authStore.hasManagementAccess) {
            navigation.goToHome();
        }
    }, [authStore.hasManagementAccess, navigation]);

    if (!authStore.hasManagementAccess) {
        return null;
    }

    const onEditionChange = (id: string) => {
        select(id);
        search.setParams({
            period: undefined,
            startDate: undefined,
            endDate: undefined,
        });
    };

    const renderCard = (spec: KpiCardSpec) => (
        <KpiCardFromSpec
            key={spec.title}
            spec={{ ...spec, campaignId: edition?.id }}
            pageRange={filter.range}
            pageRegion={filter.provinces}
            descriptionOnlyInfo
            compareEnabled={filter.compare}
            waveKey={filter.waveKey}
            campaign={context}
        />
    );

    return (
        <>
            <PageTopBar
                title="NHL Coins - Analytics"
                titleExtra={
                    edition && (
                        <Select<string>
                            className={style.edition_select}
                            value={edition.id}
                            onChange={onEditionChange}
                            options={editions.map((served) => ({
                                value: served.id,
                                label: served.id,
                            }))}
                            aria-label="Edition"
                        />
                    )
                }
            />

            <div className={`${style.dashboard} content_with_margins`}>
                <OverviewToolbar
                    filters={
                        <AnalyticsSearchOptions
                            startDate={filter.range.start}
                            endDate={filter.range.end}
                            updateParams={setPeriod}
                            lastSelectableDay={lastSelectableDay()}
                            periodPresets={{
                                active: filter.preset,
                                onSelect: setPreset,
                                campaign,
                            }}
                            compareEnabled={filter.compare}
                            onCompareChange={setCompare}
                            regionFilter={{
                                value: filter.provinces,
                                disabled: filter.regionLocked,
                                onChange: setRegion,
                            }}
                        />
                    }
                />

                <KpiLoadProgressBar />

                <section className={style.section}>
                    <SectionHeader title="Enrolled players" />
                    <div className={style.grid}>
                        {ENROLLED_PLAYERS_CARDS.map(renderCard)}
                    </div>
                </section>

                <section className={style.section}>
                    <SectionHeader title="Booklet collection" />
                    <div className={style.grid}>
                        {BOOKLET_COLLECTION_CARDS.map(renderCard)}
                    </div>
                </section>

                <section className={style.section}>
                    <SectionHeader title="Coin popularity" />
                    <div className={style.grid}>
                        {COIN_POPULARITY_CARDS.map(renderCard)}
                    </div>
                </section>
            </div>
        </>
    );
});
